import React from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Mail } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { isFrPath, frTwinFor, enTwinFor, localizedPath } from "@/i18n/paths";
import { TESTIDS } from "@/constants/testIds";

export const CONTACT_PHONE = "(807) 907-2874";
export const CONTACT_PHONE_TEL = "+18079072874";
export const CONTACT_EMAIL = "info@mazesecurity.ca";

export const ContactStrip = ({ variant = "top" }) => (
  <div
    className={`w-full ${variant === "top" ? "bg-amber-500 text-black" : "bg-black text-amber-500 border-t border-white/10"}`}
    data-testid="contact-strip"
  >
    <div className="max-w-[1600px] mx-auto px-4 md:px-10 h-9 flex items-center justify-between md:justify-end gap-4 md:gap-8 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.15em] md:tracking-[0.2em]">
      <a
        href={`tel:${CONTACT_PHONE_TEL}`}
        data-testid="contact-strip-phone"
        className="inline-flex items-center gap-2 hover:opacity-70 transition-opacity whitespace-nowrap"
      >
        <Phone className="w-3 h-3 md:w-3.5 md:h-3.5" strokeWidth={2.2} />
        <span>{CONTACT_PHONE}</span>
      </a>
      <a
        href={`mailto:${CONTACT_EMAIL}`}
        data-testid="contact-strip-email"
        className="inline-flex items-center gap-2 hover:opacity-70 transition-opacity truncate"
      >
        <Mail className="w-3 h-3 md:w-3.5 md:h-3.5" strokeWidth={2.2} />
        <span className="truncate">{CONTACT_EMAIL}</span>
      </a>
    </div>
  </div>
);

export const Nav = () => {
  const { t, lang } = useLang();
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const loc = useLocation();
  const navigate = useNavigate();

  // Language switch navigates to the paired EN/FR URL (hreflang twins)
  const toggleLang = () => {
    if (isFrPath(loc.pathname)) {
      navigate(enTwinFor(loc.pathname));
    } else {
      navigate(frTwinFor(loc.pathname) || "/fr");
    }
  };

  React.useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  React.useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  const links = [
    { to: localizedPath("/services", lang), label: t("nav.services"), tid: TESTIDS.nav.services },
    { to: "/cities", label: t("nav.cities"), tid: TESTIDS.nav.cities },
    { to: "/case-studies", label: t("nav.case_studies"), tid: "nav-case-studies-link" },
    { to: "/insights", label: t("nav.insights"), tid: TESTIDS.nav.insights },
    { to: localizedPath("/about", lang), label: t("nav.about"), tid: TESTIDS.nav.about },
    { to: localizedPath("/contact", lang), label: t("nav.contact"), tid: TESTIDS.nav.contact },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background,backdrop-filter,border] duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/85 border-b border-white/10"
          : "bg-black border-b border-transparent"
      }`}
    >
      {/* Persistent amber contact strip — visible on every page + mobile */}
      <ContactStrip variant="top" />

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
        <Link
          to={localizedPath("/", lang)}
          data-testid={TESTIDS.nav.logo}
          className="group flex items-center gap-3"
        >
          <span className="relative w-9 h-9 flex items-center justify-center bg-amber-500 text-black font-display font-black text-2xl leading-none group-hover:bg-amber-400 transition-colors shrink-0" aria-hidden>
            M
          </span>
          <span className="font-display uppercase tracking-[0.14em] text-[11px] md:text-[13px] text-white leading-tight">
            Maze Security Solutions<br />
            <span className="text-zinc-500 text-[9px] md:text-[11px] normal-case tracking-[0.12em]">Quebec Security Services</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.tid}
              className={({ isActive }) =>
                `text-[12px] font-mono uppercase tracking-[0.2em] transition-colors duration-300 hover:text-amber-500 ${
                  isActive ? "text-amber-500" : "text-zinc-300"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid={TESTIDS.nav.langToggle}
            onClick={toggleLang}
            aria-label="Toggle language"
            className="hidden md:flex items-center gap-1.5 border border-white/15 px-3 h-9 text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-300 hover:border-amber-500 hover:text-amber-500 transition-colors duration-300"
          >
            <span className={lang === "en" ? "text-amber-500" : ""}>EN</span>
            <span className="text-zinc-600">/</span>
            <span className={lang === "fr" ? "text-amber-500" : ""}>FR</span>
          </button>

          <button
            data-testid={TESTIDS.nav.mobileMenu}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 flex items-center justify-center border border-white/15 text-white"
            aria-label="Menu"
          >
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`${l.tid}-mobile`}
                  className="text-xl font-display uppercase tracking-tight text-white hover:text-amber-500 transition-colors"
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="mt-2 pt-4 border-t border-white/10 space-y-3">
                <a href={`tel:${CONTACT_PHONE_TEL}`} className="flex items-center gap-3 text-white">
                  <Phone className="w-4 h-4 text-amber-500" /> {CONTACT_PHONE}
                </a>
                <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-3 text-white">
                  <Mail className="w-4 h-4 text-amber-500" /> {CONTACT_EMAIL}
                </a>
                <button
                  onClick={toggleLang}
                  className="w-full border border-white/15 h-11 text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-300 mt-2"
                >
                  {lang.toUpperCase()} / {lang === "en" ? "FR" : "EN"}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
